import { Event } from '@/util/types/serverTypes';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import dayjs from 'dayjs';

export type ManageWorkingHoldParams = {
  currentHold: Event | null;
  inspectorId: string | null;
  datetime: string | null; // ISO string
  durationHours: number;
  expiresInMinutes?: number;
  skipCondition?: () => boolean; // For component-specific checks
  jwtToken?: string; // Optional JWT token for online scheduler
  companyId?: string; // Optional company ID for online scheduler
};

export type ManageWorkingHoldResult = {
  hold: Event | null;
  error?: string;
};

/**
 * Manages working holds for scheduler slots.
 * Deletes existing hold if present, then creates a new one with the provided parameters.
 *
 * @param params - Configuration for hold management
 * @returns Object containing the created hold (or null) and optional error message
 */
export async function manageWorkingHold(
  params: ManageWorkingHoldParams
): Promise<ManageWorkingHoldResult> {
  const {
    currentHold,
    inspectorId,
    datetime,
    durationHours,
    expiresInMinutes = 5,
    skipCondition,
    jwtToken,
    companyId,
  } = params;

  try {
    // Check if we should skip hold creation (component-specific logic)
    if (skipCondition && skipCondition()) {
      return { hold: null };
    }

    // Delete existing hold if present
    if (currentHold) {
      await callServerAction({
        url: `event/${currentHold._id}`,
        method: 'delete',
        params: jwtToken && companyId ? { _companyId: companyId } : undefined,
        headers: jwtToken ? { 'client-token': jwtToken } : undefined,
      }).catch(() => {
        // Silently handle delete errors - hold might already be expired
      });
    }

    // Validate required parameters
    if (!inspectorId || !datetime) {
      return { hold: null };
    }

    // Create new working hold
    const startTime = dayjs(datetime);
    const endTime = startTime.add(durationHours, 'hour').subtract(1, 'seconds');

    // Calculate expiration time for the name
    const expirationTime = dayjs().add(expiresInMinutes, 'minute');
    const expirationTimeFormatted = expirationTime.format('h:mma');

    const hold = await callServerAction<Event>({
      url: 'event',
      method: 'post',
      data: {
        _inspectorId: inspectorId,
        datetime: startTime.toISOString(),
        endtime: endTime.toISOString(),
        name: `Working Block (expires ${expirationTimeFormatted})`,
        type: 'Block',
        description: 'Slot has been selected in the scheduler. Working...',
        expiresInMinutes,
      },
      params: jwtToken && companyId ? { _companyId: companyId } : undefined,
      headers: jwtToken ? { 'client-token': jwtToken } : undefined,
    });

    return { hold };
  } catch (error) {
    console.error('Error managing working hold:', error);
    return {
      hold: null,
      error: error instanceof Error ? error.message : 'Error managing hold',
    };
  }
}
